import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Calculator, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const Brand = () => (
  <div className="mb-8 flex items-center justify-center gap-2">
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground">
      <Calculator className="h-6 w-6" />
    </div>
    <span className="font-display text-2xl font-extrabold tracking-tight text-foreground">
      TilloCash
    </span>
  </div>
);

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md">
        <Brand />
        <Card className="rounded-2xl border-border p-8">
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-foreground">
            Log in
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Access your business's cash counts.
          </p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email" className="text-sm font-semibold">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-1.5 h-12"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-semibold">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-1.5 h-12"
                required
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm font-medium text-rose-600">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="h-12 w-full rounded-xl font-bold active:scale-[0.98]"
            >
              {loading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
              Log in
            </Button>
          </form>
        </Card>
        <p className="mt-5 text-center text-sm text-muted-foreground">
          New here?{" "}
          <Link to="/signup" data-testid="go-to-signup-link" className="font-semibold text-primary hover:underline">
            Create a business account
          </Link>
        </p>
      </div>
    </div>
  );
}
